import type { Key } from '@vizit/atoms';
import { FormComboBox } from '@vizit/forms';
import { useDebounce } from '@vizit/hooks';
import { type CategoryItem, ComboBox } from '@vizit/molecules';
import * as React from 'react';
import type { Control, FieldValues, Path } from 'react-hook-form';
import { useController } from 'react-hook-form';

type InputFieldState = {
  /**
   * Currently selected key
   *
   * @type {Key | null}
   */
  selectedKey: Key | null;
  /**
   * Current input value
   *
   * @type {string}
   */
  inputValue: string;
};

interface FormProductCategoryComboBoxProps<T extends FieldValues> {
  /**
   * The menu trigger for the combo box.
   */
  menuTrigger?: 'input' | 'focus';
  /**
   * The control for the form.
   */
  control: Control<T>;
  /**
   * The name of the field in the form.
   */
  name: Path<T>;
  /**
   * The label for the combo box.
   */
  label?: string;
  /**
   * The class name for the combo box.
   */
  className?: string;
  /**
   * The filter text for the combo box.
   */
  filterText: string;
  /**
   * The items for the combo box.
   */
  items: CategoryItem[];
  /**
   * Whether the combo box is loading.
   */
  isLoading: boolean;
  /**
   * Whether the combo box is required.
   */
  isRequired?: boolean;
  /**
   * Whether the combo box is disabled.
   */
  isDisabled?: boolean;
  /**
   * Helper text shown under the field, e.g. to say the category can be chosen later.
   */
  description?: string;
  /**
   * The function to set the filter text.
   */
  setFilterText: (text: string) => void;
  /**
   * The function to load more items.
   */
  loadMore: () => void;
  /**
   * Test ID for E2E tests
   */
  'data-testid'?: string;
}

/**
 * Product category combo box component that integrates with react-hook-form
 * and provides infinite scrolling with filtering capabilities.
 *
 * This component wraps the FormComboBox and integrates with useProductCategoryList hook
 * for data management.
 *
 * @param props - Component props
 * @returns JSX element
 */
function FormProductCategoryComboBox<T extends FieldValues>(props: FormProductCategoryComboBoxProps<T>) {
  const {
    control,
    name,
    label,
    isRequired,
    isDisabled,
    description,
    className,
    items,
    isLoading,
    filterText,
    setFilterText,
    loadMore,
    menuTrigger = 'focus',
    'data-testid': testId,
  } = props;

  const { field, formState } = useController({ name, control });

  const defaultSelectedKey = React.useMemo(
    () => (formState?.defaultValues?.[name] ? formState?.defaultValues?.[name] : undefined),
    [formState?.defaultValues, name],
  );
  const defaultInputValue = React.useMemo(
    () =>
      formState?.defaultValues?.[name] ? items.find((item) => item.id === formState?.defaultValues?.[name])?.name : '',
    [formState?.defaultValues, name, items],
  );

  const [inputFieldState, setInputFieldState] = React.useState<InputFieldState>({
    selectedKey: defaultSelectedKey || null,
    inputValue: filterText || defaultInputValue || '',
  });

  const [localFilterText, setLocalFilterText] = React.useState(filterText || '');
  const debouncedFilterText = useDebounce(localFilterText, 300);

  // Only update the parent's filterText when debounced value changes
  React.useEffect(() => {
    setFilterText(debouncedFilterText);
  }, [debouncedFilterText, setFilterText]);

  const onSelectionChange = (key: Key | null) => {
    const inputValue = items.find((item) => item.id === key)?.name || '';

    setInputFieldState({
      selectedKey: key,
      inputValue,
    });
    // This is a temporary fix
    // TODO: https://linear.app/vizit/issue/SPM-1218/story-productcategory-combobox-simplify-state-handling-with-lodash
    // setLocalFilterText(inputValue);
    // setFilterText(inputValue);
  };

  const onInputChange = (inputValue: string) => {
    setInputFieldState((prevState: InputFieldState) => ({
      inputValue,
      selectedKey: inputValue === '' ? null : prevState.selectedKey,
    }));

    setLocalFilterText(inputValue);
  };

  // Sync field value with input value
  React.useEffect(() => {
    if (!field.value) {
      setInputFieldState({
        selectedKey: null,
        inputValue: '',
      });

      setLocalFilterText('');
      setFilterText('');
    }
  }, [field.value, setFilterText]);

  const renderEmptyState = React.useCallback(() => {
    if (isLoading) return null;
    return <ComboBox.Empty>No categories found</ComboBox.Empty>;
  }, [isLoading]);

  return (
    <FormComboBox<T, CategoryItem>
      renderEmptyState={renderEmptyState}
      control={control}
      name={name}
      label={label}
      isRequired={isRequired}
      isDisabled={isDisabled}
      description={description}
      className={className}
      isLoading={isLoading}
      inputValue={inputFieldState.inputValue}
      onInputChange={onInputChange}
      onLoadMore={loadMore}
      items={items}
      handleSelectedKey={onSelectionChange}
      controlledSelectedKey={inputFieldState.selectedKey}
      placeholder="Choose category"
      menuTrigger={menuTrigger}
      popoverProps={{
        shouldFlip: false,
      }}
      data-testid={testId}
    >
      {(item: CategoryItem) => <ComboBox.ProductCategoryItem key={item.id} {...item} />}
    </FormComboBox>
  );
}

export { FormProductCategoryComboBox };
export type { FormProductCategoryComboBoxProps };
